import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Plus, FileText, Download, CheckCircle, XCircle,
  X, Loader2, Shield, Eye, Filter, Ban
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import InvoicePortalLayout from '@/components/invoice/InvoicePortalLayout';

const TAX_RATES = [
  { value: 4, label: '4% (Superreducido)' },
  { value: 10, label: '10% (Reducido)' },
  { value: 21, label: '21% (General)' },
];

const PAYMENT_METHODS = ['bank_transfer', 'cash', 'card', 'other'];
const emptyItem = { description: '', quantity: 1, price: '', tax_rate: 21 };

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600',
  issued: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
};

const InvoiceManagePage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  const [customer, setCustomer] = useState({ name: '', nif: '', address: '', city: '', postal_code: '', email: '' });
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const shopsRes = await api.get('/shops');
      if (shopsRes.data.length === 0) { navigate('/invoices/setup'); return; }
      const s = shopsRes.data[0];
      setShop(s);
      const invRes = await api.get(`/shops/${s.id}/invoices`);
      setInvoices(invRes.data);
    } catch (e) { console.error('Failed to load invoices:', e); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => invoices.filter(inv => {
    if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return inv.invoice_number.toLowerCase().includes(q) ||
        inv.customer?.name?.toLowerCase().includes(q) ||
        inv.customer?.nif?.toLowerCase().includes(q);
    }
    return true;
  }), [invoices, statusFilter, searchQuery]);

  const calcTotals = () => {
    let subtotal = 0, taxTotal = 0;
    items.forEach(item => {
      const line = (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0);
      subtotal += line;
      taxTotal += line * (item.tax_rate / 100);
    });
    return { subtotal, taxTotal, total: subtotal + taxTotal };
  };

  const resetForm = () => {
    setCustomer({ name: '', nif: '', address: '', city: '', postal_code: '', email: '' });
    setItems([{ ...emptyItem }]);
    setPaymentMethod('bank_transfer');
    setDueDate('');
    setNotes('');
  };

  const handleCreate = async () => {
    if (!customer.name || items.length === 0 || !items[0].description || !items[0].price) return;
    setSaving(true);
    try {
      await api.post(`/shops/${shop.id}/invoices`, {
        customer,
        items: items.map(i => ({ ...i, price: parseFloat(i.price), quantity: parseInt(i.quantity) })),
        payment_method: paymentMethod,
        due_date: dueDate || null,
        notes: notes || null,
      });
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error creating invoice'); }
    finally { setSaving(false); }
  };

  const handleMarkPaid = async (invId) => {
    try { await api.post(`/shops/${shop.id}/invoices/${invId}/mark-paid`); fetchData(); setShowDetail(null); }
    catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleCancel = async (invId) => {
    try { await api.post(`/shops/${shop.id}/invoices/${invId}/cancel`); fetchData(); setShowDetail(null); }
    catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleDownloadPDF = async (invId, invNumber) => {
    try {
      const res = await api.get(`/shops/${shop.id}/invoices/${invId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${invNumber}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Error downloading PDF'); }
  };

  const addItem = () => setItems([...items, { ...emptyItem }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => {
    const copy = [...items]; copy[i] = { ...copy[i], [field]: val }; setItems(copy);
  };

  const totals = calcTotals();
  const statusLabel = (s) => ({ draft: t('inv_draft'), issued: t('inv_issued'), paid: t('inv_paid'), cancelled: t('inv_cancelled') }[s] || s);
  const payLabel = (p) => ({ bank_transfer: t('inv_bank'), cash: t('inv_cash'), card: t('inv_card'), other: t('inv_other') }[p] || p);

  if (loading) {
    return <InvoicePortalLayout><div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" /></div></InvoicePortalLayout>;
  }

  return (
    <InvoicePortalLayout>
      <div className="space-y-6" data-testid="invoice-manage-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#1e3a5f]">{t('inv_title') || 'Invoices'}</h2>
            <p className="text-slate-500 text-sm">{invoices.length} {(t('inv_title') || 'invoices').toLowerCase()}</p>
          </div>
          <button onClick={() => { resetForm(); setShowCreate(true); }}
            className="vf-btn-primary flex items-center gap-2" data-testid="portal-new-invoice-btn">
            <Plus className="w-5 h-5" /> {t('inv_create') || 'New Invoice'}
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('inv_search') || 'Search invoices...'}
              className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
              data-testid="portal-invoices-search" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {['all', 'draft', 'issued', 'paid', 'cancelled'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-4 py-2 rounded-sm whitespace-nowrap text-sm ${statusFilter === s ? 'bg-[#1e3a5f] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                {s === 'all' ? (t('inv_all') || 'All') : statusLabel(s)}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 bg-white border-2 border-slate-100">
            <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 text-lg">{t('inv_no_invoices') || 'No invoices'}</p>
            <button onClick={() => { resetForm(); setShowCreate(true); }} className="vf-btn-primary inline-flex items-center gap-2 mt-4">
              <Plus className="w-5 h-5" /> {t('inv_create') || 'New Invoice'}
            </button>
          </div>
        ) : (
          <div className="bg-white border-2 border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="portal-invoices-table">
                <thead>
                  <tr className="border-b-2 border-slate-100 text-left">
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('inv_number') || '#'}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('inv_date') || 'Date'}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('inv_customer') || 'Customer'}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('inv_total') || 'Total'}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('inv_status') || 'Status'}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('inv_verifactu') || 'VeriFactu'}</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(inv => (
                    <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => setShowDetail(inv)}>
                      <td className="px-4 py-3 font-mono font-semibold text-[#1e3a5f]">{inv.invoice_number}</td>
                      <td className="px-4 py-3 text-slate-600">{inv.issue_date}</td>
                      <td className="px-4 py-3"><p className="font-medium text-[#1e3a5f]">{inv.customer?.name}</p></td>
                      <td className="px-4 py-3 font-semibold text-[#22c55e]">€{inv.total?.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${STATUS_COLORS[inv.status]}`}>{statusLabel(inv.status)}</span>
                      </td>
                      <td className="px-4 py-3"><Shield className="w-4 h-4 text-[#22c55e]" /></td>
                      <td className="px-4 py-3">
                        <button onClick={(e) => { e.stopPropagation(); handleDownloadPDF(inv.id, inv.invoice_number); }}
                          className="p-2 hover:bg-slate-100 rounded-sm text-slate-500 hover:text-[#1e3a5f]">
                          <Download className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      <AnimatePresence>
        {showCreate && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowCreate(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-2 md:inset-auto md:top-[2vh] md:left-1/2 md:-translate-x-1/2 md:w-[700px] md:max-h-[96vh] bg-white z-50 flex flex-col overflow-hidden"
              data-testid="portal-create-invoice-modal">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-xl font-bold text-[#1e3a5f]">{t('inv_create') || 'New Invoice'}</h3>
                <button onClick={() => setShowCreate(false)} className="p-2 hover:bg-slate-100 rounded-sm"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-5">
                <div>
                  <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2">{t('inv_customer') || 'Customer'}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" placeholder={(t('inv_customer_name') || 'Name') + ' *'} value={customer.name}
                      onChange={e => setCustomer(c => ({ ...c, name: e.target.value }))}
                      className="col-span-2 px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                      data-testid="portal-inv-customer-name" />
                    <input type="text" placeholder={t('inv_customer_nif') || 'Tax ID (NIF)'} value={customer.nif}
                      onChange={e => setCustomer(c => ({ ...c, nif: e.target.value }))}
                      className="px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                    <input type="text" placeholder={t('inv_customer_email') || 'Email'} value={customer.email}
                      onChange={e => setCustomer(c => ({ ...c, email: e.target.value }))}
                      className="px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                    <input type="text" placeholder={t('inv_customer_address') || 'Address'} value={customer.address}
                      onChange={e => setCustomer(c => ({ ...c, address: e.target.value }))}
                      className="px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                    <div className="flex gap-2">
                      <input type="text" placeholder={t('inv_customer_city') || 'City'} value={customer.city}
                        onChange={e => setCustomer(c => ({ ...c, city: e.target.value }))}
                        className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                      <input type="text" placeholder={t('inv_customer_postal') || 'Postal'} value={customer.postal_code}
                        onChange={e => setCustomer(c => ({ ...c, postal_code: e.target.value }))}
                        className="w-24 px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2">{t('inv_items') || 'Line Items'}</h4>
                  <div className="space-y-2">
                    {items.map((item, i) => (
                      <div key={i} className="flex gap-2 items-start">
                        <input type="text" placeholder={t('inv_description') || 'Description'} value={item.description}
                          onChange={e => updateItem(i, 'description', e.target.value)}
                          className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none text-sm" />
                        <input type="number" placeholder="Qty" value={item.quantity}
                          onChange={e => updateItem(i, 'quantity', e.target.value)}
                          className="w-16 px-2 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none text-sm text-center" />
                        <input type="number" step="0.01" placeholder={t('inv_price') || 'Price'} value={item.price}
                          onChange={e => updateItem(i, 'price', e.target.value)}
                          className="w-24 px-2 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none text-sm" />
                        <select value={item.tax_rate} onChange={e => updateItem(i, 'tax_rate', parseFloat(e.target.value))}
                          className="w-24 px-2 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none text-sm">
                          {TAX_RATES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        {items.length > 1 && (
                          <button onClick={() => removeItem(i)} className="p-2 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                        )}
                      </div>
                    ))}
                    <button onClick={addItem} className="text-sm text-[#22c55e] hover:underline font-medium">+ {t('inv_add_line') || 'Add line'}</button>
                  </div>
                </div>
                <div className="bg-slate-50 p-4 space-y-1 text-sm">
                  <div className="flex justify-between"><span>{t('inv_subtotal') || 'Subtotal'}</span><span>€{totals.subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>{t('inv_tax') || 'VAT'}</span><span>€{totals.taxTotal.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold text-lg text-[#1e3a5f] pt-2 border-t border-slate-200">
                    <span>{t('inv_total') || 'Total'}</span><span>€{totals.total.toFixed(2)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_payment') || 'Payment method'}</label>
                    <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none">
                      {PAYMENT_METHODS.map(m => <option key={m} value={m}>{payLabel(m)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_due_date') || 'Due Date'}</label>
                    <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_notes') || 'Notes'}</label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none resize-none" />
                </div>
              </div>
              <div className="p-4 border-t border-slate-200 flex gap-3">
                <button onClick={() => setShowCreate(false)} className="vf-btn-secondary flex-1">{t('products_cancel') || 'Cancel'}</button>
                <button onClick={handleCreate} disabled={saving || !customer.name || !items[0]?.description || !items[0]?.price}
                  className="vf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="portal-save-invoice-btn">
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                  {t('inv_save') || 'Save Invoice'}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetail && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDetail(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="w-full md:w-[600px] max-h-[85vh] bg-white flex flex-col"
              data-testid="portal-invoice-detail-modal"
              onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-[#1e3a5f]">{showDetail.invoice_number}</h3>
                  <span className={`px-2 py-1 rounded-sm text-xs font-medium ${STATUS_COLORS[showDetail.status]}`}>{statusLabel(showDetail.status)}</span>
                </div>
                <button onClick={() => setShowDetail(null)} className="p-2 hover:bg-slate-100 rounded-sm"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-400 mb-1">{t('inv_customer') || 'Customer'}</p>
                    <p className="font-medium text-[#1e3a5f]">{showDetail.customer?.name}</p>
                    {showDetail.customer?.nif && <p className="text-sm text-slate-500">{showDetail.customer.nif}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400 mb-1">{t('inv_date') || 'Date'}</p>
                    <p className="font-medium text-[#1e3a5f]">{showDetail.issue_date}</p>
                  </div>
                </div>
                <table className="w-full text-sm border border-slate-200">
                  <thead><tr className="bg-slate-50">
                    <th className="px-3 py-2 text-left">{t('inv_description') || 'Description'}</th>
                    <th className="px-3 py-2 text-center">{t('inv_quantity') || 'Qty'}</th>
                    <th className="px-3 py-2 text-right">{t('inv_price') || 'Price'}</th>
                    <th className="px-3 py-2 text-center">{t('inv_tax_rate') || 'Tax'}</th>
                    <th className="px-3 py-2 text-right">{t('inv_line_total') || 'Total'}</th>
                  </tr></thead>
                  <tbody>
                    {showDetail.items?.map((item, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-2">{item.description}</td>
                        <td className="px-3 py-2 text-center">{item.quantity}</td>
                        <td className="px-3 py-2 text-right">€{item.price?.toFixed(2)}</td>
                        <td className="px-3 py-2 text-center">{item.tax_rate}%</td>
                        <td className="px-3 py-2 text-right font-medium">€{(item.quantity * item.price * (1 + item.tax_rate / 100)).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="bg-slate-50 p-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span>{t('inv_subtotal') || 'Subtotal'}</span><span>€{showDetail.subtotal?.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold text-lg text-[#1e3a5f] pt-2 border-t border-slate-200">
                    <span>{t('inv_total') || 'Total'}</span><span>€{showDetail.total?.toFixed(2)}</span>
                  </div>
                </div>
                <div className="bg-[#1e3a5f]/5 border border-[#1e3a5f]/20 p-3 rounded-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-[#22c55e]" />
                    <span className="text-sm font-semibold text-[#1e3a5f]">{t('inv_verifactu') || 'VeriFactu'}</span>
                  </div>
                  <p className="text-xs text-slate-500 font-mono break-all">Hash: {showDetail.verifactu_hash}</p>
                  <p className="text-xs text-slate-400 font-mono break-all mt-1">Prev: {showDetail.previous_hash}</p>
                </div>
              </div>
              <div className="p-4 border-t border-slate-200 flex gap-2 flex-wrap">
                <button onClick={() => handleDownloadPDF(showDetail.id, showDetail.invoice_number)}
                  className="vf-btn-secondary flex items-center gap-2">
                  <Download className="w-4 h-4" /> {t('inv_download_pdf') || 'Download PDF'}
                </button>
                {showDetail.status === 'issued' && (
                  <button onClick={() => handleMarkPaid(showDetail.id)}
                    className="vf-btn-primary flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> {t('inv_mark_paid') || 'Mark Paid'}
                  </button>
                )}
                {showDetail.status !== 'cancelled' && showDetail.status !== 'paid' && (
                  <button onClick={() => handleCancel(showDetail.id)}
                    className="bg-red-600 text-white px-4 py-2 text-sm font-medium hover:bg-red-700 flex items-center gap-2">
                    <Ban className="w-4 h-4" /> {t('inv_cancel_invoice') || 'Cancel'}
                  </button>
                )}
              </div>
            </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </InvoicePortalLayout>
  );
};

export default InvoiceManagePage;
